import { isNullish } from '@caresend/utils';
import { isMobile } from 'mobile-device-detect';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import { defineComponent, useSlots, ref, computed, onMounted, onUnmounted, watch, h } from 'vue';
import { useNavBarHeight } from '../../../composables/dom.js';
import { reportException } from '../../../functions/sentry.js';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../../router/helpers/initRouter.js';
import '@caresend/types';
import '../Icon.vue.js';
import '../../../functions/configuration/icons.js';
import '../../../database/algoliaConfig/indices.js';
import '../../../functions/tracking/index.js';
import { extractClassNamesFromVNode } from '../../../functions/jsx.js';

const controller = defineComponent({
  name: 'Tooltip',
  props: {
    /**
     * For devices that are reported as mobile by mobile-device-detect, tooltips
     * are normally disallowed except for on disabled elements. Pass true to
     * override this behavior, and allow the tooltip to show on tap regardless
     * of whether the target element is disabled.
     */
    alwaysAllowOnTouchDevices: Boolean,

    /**
     * Whether the tooltip should display on hover. Supports making the tooltip
     * appear/disappear without the user changing hover status.
     */
    disabled: Boolean,

    /** For debugging purposes - the tooltip will always be shown. */
    forceShow: Boolean,

    /**
     * If passed, the slot content will be always be wrapped in a dive with the
     * tooltip applied to the div. Otherwise, the tooltip will only wrap if
     * needed (if the element inside is disabled, or if there are multiple
     * elements).
     */
    forceWrap: Boolean,

    /** The text to display in the tooltip. */
    label: {
      type: String,
      default: null
    },

    /**
     * Normally, tooltips hide when `window` is scrolled. If the tooltip is used
     * in an embedded scrollable area, pass the selector for that area here.
     */
    scrollableAreaSelector: {
      type: String,
      default: null
    },

    /**
     * The position of the tooltip. https://atomiks.github.io/tippyjs/#placements
     */
    position: {
      type: String,
      default: 'top'
    }
  },

  setup(props) {
    // Need to define `h`. Auto injection is disabled as it was conflicting
    // with our manual usage. TODO: Determine better strategy.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const h$1 = h;
    const slots = useSlots();
    const tippyInstance = ref(null);
    let navBarHeight = ref(0);
    const flipTopPadding = ref(null);

    const getModifiersWithUpdatedFlip = (originalModifiers, flipEnabled) => {
      var _a, _b, _c;

      const defaultPadding = 5;
      const originalFlipModifiers = originalModifiers === null || originalModifiers === void 0 ? void 0 : originalModifiers.filter(modifier => modifier.name === 'flip');

      if (((_a = originalFlipModifiers === null || originalFlipModifiers === void 0 ? void 0 : originalFlipModifiers.length) !== null && _a !== void 0 ? _a : 0) > 1) {
        reportException('More than one flip modifier on tooltip');
      }

      const originalFlipModifier = originalFlipModifiers === null || originalFlipModifiers === void 0 ? void 0 : originalFlipModifiers[0];
      const padding = ((_b = flipTopPadding.value) !== null && _b !== void 0 ? _b : 0) > defaultPadding ? {
        top: flipTopPadding.value,
        right: defaultPadding,
        bottom: defaultPadding,
        left: defaultPadding
      } : defaultPadding;
      const flipModifier = { ...originalFlipModifier,
        name: 'flip',
        enabled: flipEnabled,
        options: { ...(originalFlipModifier === null || originalFlipModifier === void 0 ? void 0 : originalFlipModifier.options),
          padding
        }
      };

      if (originalFlipModifier) {
        const modifiersWithFlipUpdated = (_c = originalModifiers !== null && originalModifiers !== void 0 ? originalModifiers : []) === null || _c === void 0 ? void 0 : _c.map(modifier => modifier.name === 'flip' ? flipModifier : modifier);
        return modifiersWithFlipUpdated;
      }

      const modifiersWithFlipAdded = [...(originalModifiers !== null && originalModifiers !== void 0 ? originalModifiers : []), flipModifier];
      return modifiersWithFlipAdded;
    };

    const updateFlipModifier = (popperOptions, flipEnabled) => {
      var _a;

      const modifiers = getModifiersWithUpdatedFlip(popperOptions === null || popperOptions === void 0 ? void 0 : popperOptions.modifiers, flipEnabled);
      (_a = tippyInstance.value) === null || _a === void 0 ? void 0 : _a.setProps({
        popperOptions: { ...popperOptions,
          modifiers
        }
      });
    };
    /**
     * During scrolling, the tooltip may flip before it is hidden when it runs
     * out of room. This plugin prevents flipping after the tooltip is shown,
     * and helps the tooltip avoid the fixed header.
     *
     * Inspiration:
     * https://github.com/atomiks/tippyjs/issues/884#issuecomment-786626724
     */


    const enhancedFlipPlugin = {
      fn({
        props: tippyProps
      }) {
        const originalPlacement = tippyProps.placement;
        return {
          onCreate() {
            updateFlipModifier(tippyProps.popperOptions, true);
          },

          onMount({
            setProps,
            popperInstance
          }) {
            updateFlipModifier(tippyProps.popperOptions, false);
            setProps({
              placement: popperInstance === null || popperInstance === void 0 ? void 0 : popperInstance.state.placement
            });
          },

          onHidden({
            setProps
          }) {
            updateFlipModifier(tippyProps.popperOptions, true);
            setProps({
              placement: originalPlacement
            });
          }

        };
      }

    };

    const setFlipPadding = () => {
      var _a;

      const topPadding = navBarHeight.value;
      const isFirstInitWithTopPadding = !flipTopPadding.value && !!topPadding;
      flipTopPadding.value = topPadding;

      if (isFirstInitWithTopPadding) {
        updateFlipModifier((_a = tippyInstance.value) === null || _a === void 0 ? void 0 : _a.props.popperOptions, true);
      }
    };
    /**
     * Flip the tooltip if rendering it would collide with the fixed header.
     * Fixed footer support can be added in the future if needed.
     */


    const initHeaderWatching = () => {
      const {
        navBarHeight: navBarHeightComputed
      } = useNavBarHeight();
      navBarHeight = navBarHeightComputed;
      watch(() => navBarHeight.value, () => {
        setFlipPadding();
      }, {
        immediate: true
      });
    };
    /**
     * Returns null if there is no label, and no tooltip will display.
     */


    const tippyProps = computed(() => {
      var _a;

      return props.label ? {
        arrow: false,
        content: (_a = props.label) !== null && _a !== void 0 ? _a : '',
        ...(props.forceShow && {
          onHide() {
            return false;
          }

        }),
        placement: props.position,
        plugins: [enhancedFlipPlugin],
        showOnCreate: props.forceShow
      } : null;
    });
    const hasWrapper = ref(null);

    const setHasWrapper = newVal => {
      if (hasWrapper.value !== newVal) hasWrapper.value = newVal;
    };

    const firstChildMaybeDisabled = ref(false);
    const computedDisabled = computed(() => {
      if (props.disabled) return true;

      if (isMobile) {
        // The intended default behaviour is to only enable tooltips on disabled
        // elements on touch-based devices.
        if (!firstChildMaybeDisabled.value && !props.alwaysAllowOnTouchDevices) {
          return true;
        }
      }

      return props.disabled;
    });
    const classNames = computed(() => {
      const names = [];

      if (!tippyProps.value || computedDisabled.value) {
        names.push('Tooltip--disabled');
      }

      if (hasWrapper.value) names.push('Tooltip--with-wrapper');
      return names;
    });
    const templateRef = ref(null);
    const element = computed(() => {
      var _a, _b;

      if (templateRef.value instanceof Element) return templateRef.value;
      return (_b = (_a = templateRef.value) === null || _a === void 0 ? void 0 : _a.$el) !== null && _b !== void 0 ? _b : null;
    });

    const destroyTippy = () => {
      if (tippyInstance.value) {
        tippyInstance.value.destroy();
        tippyInstance.value = null;
      }
    };

    const showTooltipIfHovered = () => {
      var _a, _b;

      if ((_a = element.value) === null || _a === void 0 ? void 0 : _a.matches(':hover')) (_b = tippyInstance.value) === null || _b === void 0 ? void 0 : _b.show();
    };

    const initTippy = () => {
      destroyTippy();
      if (!element.value) throw Error('Missing element for tooltip');

      if (tippyProps.value) {
        tippyInstance.value = tippy(element.value, tippyProps.value);

        if (computedDisabled.value) {
          tippyInstance.value.disable();
        } else {
          updateFlipModifier(tippyProps.value.popperOptions, true);
        }
      }
    };

    const scrollHandler = () => {
      var _a;

      (_a = tippyInstance.value) === null || _a === void 0 ? void 0 : _a.hide();
    };

    const scollEndHandler = () => {
      showTooltipIfHovered();
    };

    const setScrollHandling = init => {
      const scrollableArea = props.scrollableAreaSelector ? document.querySelector(props.scrollableAreaSelector) : window;

      if (init) {
        scrollableArea === null || scrollableArea === void 0 ? void 0 : scrollableArea.addEventListener('scroll', scrollHandler);
        scrollableArea === null || scrollableArea === void 0 ? void 0 : scrollableArea.addEventListener('scrollend', scollEndHandler);
      } else {
        scrollableArea === null || scrollableArea === void 0 ? void 0 : scrollableArea.removeEventListener('scroll', scrollHandler);
        scrollableArea === null || scrollableArea === void 0 ? void 0 : scrollableArea.removeEventListener('scrollend', scollEndHandler);
      }
    };

    onMounted(() => {
      initTippy();
      setScrollHandling(true);
      initHeaderWatching();
    });
    onUnmounted(() => {
      destroyTippy();
      setScrollHandling(false);
    }); // If props change between nullish and non-nullish, tippy needs to be
    // created or destroyed.
    // If props change, tippy needs to be updated manually.

    watch(tippyProps, (newProps, oldProps) => {
      var _a;

      if (newProps && !oldProps) initTippy();
      if (!newProps && oldProps) destroyTippy();
      if (newProps && oldProps) (_a = tippyInstance.value) === null || _a === void 0 ? void 0 : _a.setProps(newProps);
    }); // If wrapper is added or removed, tippy needs to be reinitialized.

    watch(hasWrapper, (newVal, oldVal) => {
      if (!isNullish(oldVal) && newVal !== oldVal) initTippy();
    });
    watch(() => computedDisabled.value, () => {
      var _a, _b, _c;

      if (computedDisabled.value) {
        (_a = tippyInstance.value) === null || _a === void 0 ? void 0 : _a.hide();
        (_b = tippyInstance.value) === null || _b === void 0 ? void 0 : _b.disable();
      } else {
        (_c = tippyInstance.value) === null || _c === void 0 ? void 0 : _c.enable();
        showTooltipIfHovered();
      }
    });
    return () => {
      var _a, _b, _c, _d;

      const defaultSlot = (_a = slots.default) === null || _a === void 0 ? void 0 : _a.call(slots);
      if (!defaultSlot) throw Error('Tooltip must have child element(s)');
      const firstChild = defaultSlot[0];
      if (!firstChild) throw Error('Tooltip must have child element(s)');
      let needsWrapper = false; // Check if we can apply tooltip without adding wrapper div. We can do
      // this for single elements that are not disabled.

      if (defaultSlot.length > 1 || props.forceWrap) {
        needsWrapper = true;
      } else {
        const firstChildPropsData = (_b = firstChild.componentOptions) === null || _b === void 0 ? void 0 : _b.propsData; // The element has not rendered yet, so we can guess whether the element
        // is disabled by checking for a disabled prop that is not set to null,
        // undefined, or false. (If set to empty string or some other value,
        // assume it is disabled.)

        const maybeDisabledViaPropsDataCheck = !isNullish(firstChildPropsData === null || firstChildPropsData === void 0 ? void 0 : firstChildPropsData.disabled) && (firstChildPropsData === null || firstChildPropsData === void 0 ? void 0 : firstChildPropsData.disabled) !== false;
        const maybeDisabledViaAttrsCheck = !!((_d = (_c = firstChild === null || firstChild === void 0 ? void 0 : firstChild.data) === null || _c === void 0 ? void 0 : _c.attrs) === null || _d === void 0 ? void 0 : _d.disabled);
        firstChildMaybeDisabled.value = maybeDisabledViaPropsDataCheck || maybeDisabledViaAttrsCheck;
        if (firstChildMaybeDisabled.value) needsWrapper = true;
      } // Ensure no classnames get overridden by copying in the original
      // classNames if there will be no wrapper


      const firstChildClassNames = needsWrapper ? [] : extractClassNamesFromVNode(firstChild);
      const rootClassNames = [...firstChildClassNames, 'Tooltip', ...classNames.value]; // Render without wrapper

      if (!needsWrapper) {
        setHasWrapper(false);
        const tooltipTriggerNode = firstChild;
        tooltipTriggerNode.data = { ...tooltipTriggerNode.data,
          ref: templateRef,
          class: rootClassNames
        };
        return tooltipTriggerNode;
      } // Render with wrapper


      setHasWrapper(true);
      const wrapped = h$1("div", {
        "class": rootClassNames,
        "ref": templateRef
      }, [defaultSlot]);
      return wrapped;
    };
  }

});

export { controller };
